import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import {
  createAffiliateApplication,
  getMyAffiliatePartner,
  getPublicAffiliatePartner,
  listAdminAffiliatePartners,
  patchAdminAffiliatePartner,
  recordAffiliateEvent,
  recordAffiliateEventBeforeNavigation,
} from "../../lib/affiliateApi";
import { buildAuthReturnPath } from "../../lib/authReturn";
import "./Affiliate.css";

const IOS_FALLBACK_URL = "https://apps.apple.com/us/app/range-pocket/id6733222657";
const ANDROID_FALLBACK_URL = "https://play.google.com/store/apps/details?id=com.armorysync.rangepocket";
const ADMIN_EMAIL = "support@armorysync.com";

const partnerTypes = [
  { value: "instructor", label: "Instructor" },
  { value: "range", label: "Range" },
  { value: "creator", label: "Creator" },
  { value: "match_director", label: "Match director" },
  { value: "club", label: "Club" },
  { value: "other", label: "Other" },
];

const partnerFitItems = [
  "Instructors sending class follow-ups",
  "Ranges helping members stay organized",
  "Creators sharing practical gear workflows",
  "Clubs and match directors supporting regular shooters",
];

const referralProofItems = [
  "Inventory and accessory records",
  "Maintenance and round history",
  "Range visits, photos, and training notes",
];

function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 30);
}

function formatNumber(value) {
  return new Intl.NumberFormat().format(Number(value || 0));
}

function AuthRequiredPanel({ returnTo }) {
  const signInPath = buildAuthReturnPath("/auth/sign-in", returnTo);
  const signUpPath = buildAuthReturnPath("/auth/sign-up", returnTo);
  return (
    <section className="affiliate-panel affiliate-auth-panel">
      <div>
        <p className="affiliate-kicker">ArmorySync account required</p>
        <h1>Sign in to continue your partner setup.</h1>
        <p>Use the same ArmorySync account you use for Competition, Range Pocket, Range Grid, and future products.</p>
      </div>
      <div className="affiliate-actions">
        <Link className="affiliate-button affiliate-button-primary" to={signInPath}>Sign in</Link>
        <Link className="affiliate-button affiliate-button-secondary" to={signUpPath}>Create account</Link>
      </div>
    </section>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="affiliate-metric">
      <span>{label}</span>
      <strong>{formatNumber(value)}</strong>
    </div>
  );
}

function formatPartnerType(value) {
  return String(value || "other")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusMessage(status) {
  if (status === "active") {
    return "Approved. Your public partner link is ready to share.";
  }
  if (status === "paused") {
    return "Paused. Sharing tools are locked until ArmorySync reactivates your partner link.";
  }
  if (status === "rejected") {
    return "Not approved right now. Contact ArmorySync support if this looks wrong.";
  }
  return "Pending review. We will activate your link after ArmorySync approves the application.";
}

function PartnerProgramPage() {
  return (
    <main className="affiliate-page affiliate-public-page">
      <Helmet>
        <title>Range Pocket Partner Program | ArmorySync</title>
        <meta
          name="description"
          content="Apply for the Range Pocket Partner Program and share one clean Range Pocket download link with your students, members, subscribers, or shooting community."
        />
      </Helmet>

      <section className="affiliate-hero">
        <div>
          <p className="affiliate-kicker">Range Pocket Partner Program</p>
          <h1>Share Range Pocket with the shooters you already help.</h1>
          <p>
            Built for instructors, ranges, clubs, match directors, and creators who want a simple
            way to recommend a firearm inventory, maintenance, and range log app they can stand behind.
          </p>
          <div className="affiliate-actions">
            <Link className="affiliate-button affiliate-button-primary" to="/partners/onboarding">Apply to join</Link>
            <Link className="affiliate-button affiliate-button-secondary" to="/partners/dashboard">Partner dashboard</Link>
          </div>
        </div>
        <aside className="affiliate-hero-card">
          <span>For trusted partners</span>
          <strong>One link your audience can actually use</strong>
          <p>
            Selected partners can receive referral credit, commission terms, or co-marketing support
            through a direct agreement with ArmorySync before sharing broadly.
          </p>
        </aside>
      </section>

      <section className="affiliate-section affiliate-three-up" aria-label="How the program works">
        <article>
          <span>01</span>
          <h2>Apply with your real channel</h2>
          <p>Tell us who you help: students, members, match shooters, subscribers, or local customers.</p>
        </article>
        <article>
          <span>02</span>
          <h2>Share one clean link</h2>
          <p>Use it in class follow-ups, range newsletters, video descriptions, social bios, and club posts.</p>
        </article>
        <article>
          <span>03</span>
          <h2>Review results together</h2>
          <p>Your dashboard shows landing views and store-button clicks. App subscription review stays handled directly with ArmorySync.</p>
        </article>
      </section>

      <section className="affiliate-section affiliate-fit-list">
        <div>
          <p className="affiliate-kicker">Good fit</p>
          <h2>Best for partners who already teach, host, or explain range habits.</h2>
        </div>
        <ul>
          {partnerFitItems.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </section>

      <section className="affiliate-section affiliate-copy-card">
        <div>
          <p className="affiliate-kicker">What you can tell people</p>
          <h2>Range Pocket is for owners who want their records in one practical place.</h2>
        </div>
        <p>
          It helps track firearms, accessories, maintenance, ammo notes, range visits, target photos,
          training plans, and round history without asking your audience to rebuild everything in a spreadsheet.
          Selected partner terms are reviewed directly with ArmorySync before you share broadly.
        </p>
      </section>

      <section className="affiliate-section affiliate-disclosure-card">
        <p className="affiliate-kicker">Creator disclosure</p>
        <h2>We give approved partners disclosure-ready copy.</h2>
        <p>
          If your agreement includes credit or commission, use clear wording with your audience, such as:
          "I may receive partner credit if you download Range Pocket through this link."
        </p>
      </section>
    </main>
  );
}

function PartnerOnboardingPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [partnerType, setPartnerType] = useState("instructor");
  const [partnerCode, setPartnerCode] = useState("");
  const [partnerCodeEdited, setPartnerCodeEdited] = useState(false);
  const [websiteOrSocialUrl, setWebsiteOrSocialUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getCurrentUser()
      .then(() => {
        if (active) setSignedIn(true);
      })
      .catch(() => {
        if (active) setSignedIn(false);
      })
      .finally(() => {
        if (active) setAuthChecked(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!partnerCodeEdited) {
      setPartnerCode(slugify(displayName));
    }
  }, [displayName, partnerCodeEdited]);

  async function submitApplication(event) {
    event.preventDefault();
    if (partnerCode.length < 3) {
      setError("Partner link name must be at least 3 characters.");
      return;
    }
    if (!websiteOrSocialUrl.trim()) {
      setError("Add a website or social link so ArmorySync can review your application.");
      return;
    }
    try {
      const reviewUrl = new URL(websiteOrSocialUrl);
      if (!["http:", "https:"].includes(reviewUrl.protocol)) {
        throw new Error("Unsupported URL");
      }
    } catch {
      setError("Website or social link must be a valid http or https URL.");
      return;
    }
    if (notes.trim().length < 40) {
      setError("Add a sharing plan with at least 40 characters.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await createAffiliateApplication({
        displayName,
        partnerType,
        partnerCode,
        websiteOrSocialUrl,
        notes,
      });
      navigate("/partners/dashboard", { replace: true });
    } catch (submitError) {
      setError(submitError?.message || "Could not submit partner application.");
    } finally {
      setBusy(false);
    }
  }

  if (!authChecked) {
    return <main className="affiliate-page"><div className="affiliate-loading">Checking account...</div></main>;
  }

  if (!signedIn) {
    return <main className="affiliate-page"><AuthRequiredPanel returnTo="/partners/onboarding" /></main>;
  }

  return (
    <main className="affiliate-page affiliate-form-page">
      <Helmet>
        <title>Partner Onboarding | ArmorySync</title>
      </Helmet>
      <section className="affiliate-form-shell">
        <div>
          <p className="affiliate-kicker">Partner onboarding</p>
          <h1>Create your partner application.</h1>
          <p>
            Use the name your audience already recognizes. After approval, your dashboard will give you
            one partner link to share in your normal teaching, range, club, or content workflow.
          </p>
          <div className="affiliate-application-guide">
            <span>A strong application includes</span>
            <ul>
              <li>Who your audience is</li>
              <li>Where you plan to share</li>
              <li>How Range Pocket fits your workflow</li>
            </ul>
          </div>
        </div>
        <form className="affiliate-form" onSubmit={submitApplication}>
          <label>
            Partner display name
            <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} required />
            <span className="affiliate-field-help">Example: Bellevue Defensive Training, John Range, or Armory Notes.</span>
          </label>
          <label>
            Partner type
            <select value={partnerType} onChange={(event) => setPartnerType(event.target.value)}>
              {partnerTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </label>
          <label>
            Partner link name
            <input
              value={partnerCode}
              onChange={(event) => {
                setPartnerCodeEdited(true);
                setPartnerCode(slugify(event.target.value));
              }}
              minLength={3}
              maxLength={30}
              required
            />
            <span className="affiliate-field-help">This becomes the end of your public link, like armorysync.com/r/your-name. Use 3-30 lowercase letters, numbers, or hyphens.</span>
          </label>
          <label>
            Website or social link
            <input type="url" value={websiteOrSocialUrl} onChange={(event) => setWebsiteOrSocialUrl(event.target.value)} placeholder="https://youtube.com/@yourchannel" required />
            <span className="affiliate-field-help">Use the main place where your audience knows you.</span>
          </label>
          <label>
            Sharing plan
            <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={5} minLength={40} placeholder="Example: I teach 2 handgun classes per month and would share Range Pocket in post-class emails for students who need maintenance logs and range visit history." required />
          </label>
          {error && <div className="affiliate-error">{error}</div>}
          <button className="affiliate-button affiliate-button-primary" type="submit" disabled={busy}>
            {busy ? "Submitting..." : "Submit application"}
          </button>
        </form>
      </section>
    </main>
  );
}

function PartnerDashboardPage() {
  const [state, setState] = useState({ loading: true, signedIn: false, partner: null, error: "" });
  const [copyMessage, setCopyMessage] = useState("");

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        await getCurrentUser();
      } catch (error) {
        if (active) setState({ loading: false, signedIn: false, partner: null, error: error?.message || "" });
        return;
      }

      try {
        const payload = await getMyAffiliatePartner();
        if (active) setState({ loading: false, signedIn: true, partner: payload.data, error: "" });
      } catch (error) {
        if (active) setState({ loading: false, signedIn: true, partner: null, error: error?.message || "Could not load partner dashboard." });
      }
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  async function copyText(value, label) {
    try {
      await navigator.clipboard.writeText(value);
      setCopyMessage(`${label} copied`);
    } catch {
      setCopyMessage(`Could not copy automatically. Select and copy the ${label.toLowerCase()} manually.`);
    }
    setTimeout(() => setCopyMessage(""), 2200);
  }

  if (state.loading) {
    return <main className="affiliate-page"><div className="affiliate-loading">Loading partner dashboard...</div></main>;
  }

  if (!state.signedIn) {
    return <main className="affiliate-page"><AuthRequiredPanel returnTo="/partners/dashboard" /></main>;
  }

  if (state.error && !state.partner) {
    return (
      <main className="affiliate-page">
        <section className="affiliate-panel">
          <p className="affiliate-kicker">Dashboard unavailable</p>
          <h1>We could not load your partner dashboard.</h1>
          <p>{state.error}</p>
          <button className="affiliate-button affiliate-button-primary" type="button" onClick={() => window.location.reload()}>Try again</button>
        </section>
      </main>
    );
  }

  if (!state.partner) {
    return (
      <main className="affiliate-page">
        <section className="affiliate-panel">
          <p className="affiliate-kicker">No partner application yet</p>
          <h1>Apply before copying your partner link.</h1>
          <p>Once approved, your dashboard will show the one link to share, optional direct store links, and partner traffic basics.</p>
          <Link className="affiliate-button affiliate-button-primary" to="/partners/onboarding">Start application</Link>
        </section>
      </main>
    );
  }

  const metrics = state.partner.metrics || {};
  const iosReady = Boolean(state.partner.links?.ios);
  const androidReady = Boolean(state.partner.links?.android);
  const partnerIsActive = state.partner.status === "active";
  const captionOptions = [
    {
      label: "Instructor follow-up",
      copy: `For students who want a cleaner way to keep firearm inventory, maintenance, range visit history, and training notes together, I recommend Range Pocket: ${state.partner.shareUrl}`,
    },
    {
      label: "Creator post",
      copy: `I use Range Pocket as a practical firearm record and range log app: inventory, accessories, maintenance, target photos, and training notes in one place. Download it here: ${state.partner.shareUrl}`,
    },
    {
      label: "Disclosure",
      copy: `Disclosure: I may receive partner credit if you download Range Pocket through this link. ${state.partner.shareUrl}`,
    },
  ];

  return (
    <main className="affiliate-page">
      <Helmet>
        <title>Partner Dashboard | ArmorySync</title>
      </Helmet>
      <section className="affiliate-dashboard-hero">
        <div>
          <p className="affiliate-kicker">Partner dashboard</p>
          <h1>{state.partner.displayName}</h1>
          <p><span className={`affiliate-status affiliate-status-${state.partner.status}`}>{state.partner.status}</span> {statusMessage(state.partner.status)}</p>
        </div>
        {partnerIsActive ? (
          <Link className="affiliate-button affiliate-button-secondary" to={`/r/${state.partner.partnerCode}`}>Preview landing page</Link>
        ) : (
          <button className="affiliate-button affiliate-button-secondary" type="button" disabled>Preview after approval</button>
        )}
      </section>

      {partnerIsActive ? (
        <>
          <section className="affiliate-copy-card affiliate-dashboard-note">
            <div>
              <p className="affiliate-kicker">Best link to share</p>
              <h2>Use the partner landing page first.</h2>
            </div>
            <p>
              The public partner link works for iOS and Android visitors, gives your audience a short explanation
              of Range Pocket, and keeps landing views plus store-button clicks in one report. Direct store links are optional backups.
            </p>
          </section>

          <section className="affiliate-link-board">
            <div className="affiliate-link-row">
              <span>Primary partner link</span>
              <code>{state.partner.shareUrl}</code>
              <button onClick={() => copyText(state.partner.shareUrl, "Share link")}>Copy</button>
            </div>
            <div className="affiliate-link-row">
              <span>Direct App Store link</span>
              <code>{iosReady ? state.partner.links.ios : "Pending Apple campaign config. Use the primary partner link for now."}</code>
              <button disabled={!iosReady} onClick={() => copyText(state.partner.links.ios, "iOS link")}>{iosReady ? "Copy" : "Pending"}</button>
            </div>
            <div className="affiliate-link-row">
              <span>Direct Google Play link</span>
              <code>{androidReady ? state.partner.links.android : "Android package config needed"}</code>
              <button disabled={!androidReady} onClick={() => copyText(state.partner.links.android, "Android link")}>{androidReady ? "Copy" : "Pending"}</button>
            </div>
            {copyMessage && <div className="affiliate-copy-note">{copyMessage}</div>}
          </section>

          <section className="affiliate-caption-grid">
            {captionOptions.map((caption) => (
              <article key={caption.label} className="affiliate-share-script">
                <div>
                  <p className="affiliate-kicker">{caption.label}</p>
                  <p>{caption.copy}</p>
                </div>
                <button onClick={() => copyText(caption.copy, caption.label)}>Copy</button>
              </article>
            ))}
          </section>

          <section className="affiliate-share-checklist" aria-label="Suggested places to share">
            <article>
              <span>Use it in</span>
              <strong>Class recap emails</strong>
            </article>
            <article>
              <span>Use it in</span>
              <strong>Video descriptions</strong>
            </article>
            <article>
              <span>Use it in</span>
              <strong>Range newsletters</strong>
            </article>
          </section>
        </>
      ) : (
        <section className="affiliate-panel affiliate-dashboard-note">
          <p className="affiliate-kicker">Sharing locked</p>
          <h2>Wait for approval before sharing your partner link.</h2>
          <p>
            Your public partner page is activated only after approval. This keeps your audience from landing
            on a generic page and keeps reporting tied to approved partner links.
          </p>
        </section>
      )}

      <section className="affiliate-metrics-grid" aria-label="Partner metrics">
        <MetricCard label="Landing views" value={metrics.pageViews} />
        <MetricCard label="iOS campaign clicks" value={metrics.iosClicks} />
        <MetricCard label="iOS standard clicks" value={metrics.iosStandardClicks} />
        <MetricCard label="Android clicks" value={metrics.androidClicks} />
        <MetricCard label="Android standard clicks" value={metrics.androidStandardClicks} />
        <MetricCard label="Outbound clicks" value={metrics.outboundClicks} />
      </section>
    </main>
  );
}

function ReferralLandingPage() {
  const { partnerCode } = useParams();
  const [state, setState] = useState({ loading: true, partner: null, error: "" });

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const payload = await getPublicAffiliatePartner(partnerCode);
        if (!active) return;
        setState({ loading: false, partner: payload.data, error: "" });
        recordAffiliateEvent({
          partnerCode,
          eventType: "page_view",
          sourcePath: window.location.pathname,
          referrer: document.referrer,
        }).catch(() => {});
      } catch (error) {
        if (active) setState({ loading: false, partner: null, error: error?.message || "Partner link unavailable" });
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [partnerCode]);

  const partnerName = state.partner?.displayName || "an ArmorySync partner";
  const iosCampaignReady = Boolean(state.partner?.links?.ios);
  const androidCampaignReady = Boolean(state.partner?.links?.android);
  const iosUrl = state.partner?.links?.ios || IOS_FALLBACK_URL;
  const androidUrl = state.partner?.links?.android || ANDROID_FALLBACK_URL;
  const storeButtonsDisabled = state.loading;
  const linkUnavailable = !state.loading && !state.partner;

  function trackClick(eventType, destinationUrl) {
    if (state.loading) return;
    recordAffiliateEventBeforeNavigation({
      partnerCode: state.partner?.partnerCode || partnerCode,
      eventType,
      sourcePath: window.location.pathname,
      destinationUrl,
      referrer: document.referrer,
    });
  }

  if (linkUnavailable) {
    return (
      <main className="affiliate-page affiliate-referral-page">
        <Helmet>
          <title>Partner Link Unavailable | ArmorySync</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <section className="affiliate-referral-hero affiliate-referral-closed">
          <div>
            <p className="affiliate-kicker">Partner link unavailable</p>
            <h1>This Range Pocket partner link is not active.</h1>
            <p>
              The link may be pending approval, paused, or no longer available. Please ask the partner for
              an updated link or visit ArmorySync directly.
            </p>
            <div className="affiliate-actions">
              <a className="affiliate-button affiliate-button-secondary" href="/partners">Partner program</a>
              <a className="affiliate-button affiliate-button-secondary" href="/range-pocket">Range Pocket</a>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="affiliate-page affiliate-referral-page">
      <Helmet>
        <title>Range Pocket Recommended by {partnerName} | ArmorySync</title>
        <meta name="description" content="Download Range Pocket, the firearm inventory, maintenance, and range log app recommended by an ArmorySync partner." />
      </Helmet>
      <section className="affiliate-referral-hero">
        <div>
          <p className="affiliate-kicker">{state.partner ? `Recommended by ${partnerName}` : "Range Pocket"}</p>
          <h1>Keep firearm inventory, maintenance, and range history in one practical app.</h1>
          <p>
            Range Pocket helps responsible firearm owners track firearms, accessories,
            ammo, range visits, target photos, maintenance, and training notes without
            keeping a spreadsheet alive.
          </p>
          <div className="affiliate-actions">
            {storeButtonsDisabled ? (
              <>
                <button className="affiliate-button affiliate-button-primary" type="button" disabled>Loading link</button>
                <button className="affiliate-button affiliate-button-secondary" type="button" disabled>Loading link</button>
              </>
            ) : (
              <>
                <a className="affiliate-button affiliate-button-primary" href={iosUrl} onClick={() => trackClick(iosCampaignReady ? "ios_click" : "ios_standard_click", iosUrl)}>
                  App Store
                </a>
                <a className="affiliate-button affiliate-button-secondary" href={androidUrl} onClick={() => trackClick(androidCampaignReady ? "android_click" : "android_standard_click", androidUrl)}>
                  Google Play
                </a>
              </>
            )}
          </div>
          <p className="affiliate-referral-note">
            {state.partner
              ? `${partnerName} shared this because Range Pocket fits the records regular shooters often need after classes, range visits, and maintenance work.`
              : "Range Pocket is built for the records regular shooters often need after classes, range visits, and maintenance work."}
          </p>
          <ul className="affiliate-proof-list">
            {referralProofItems.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </div>
        <div className="affiliate-phone-stack" aria-hidden="true">
          <img src="/features/feature-armory-status.webp" alt="" />
          <img src="/features/feature-range-visits.webp" alt="" />
        </div>
      </section>
    </main>
  );
}

function PartnerAdminPage() {
  const [state, setState] = useState({ loading: true, signedIn: false, isAdmin: false, partners: [], nextToken: "", loadingMore: false, error: "" });
  const [reviewNotes, setReviewNotes] = useState({});
  const [reviewErrors, setReviewErrors] = useState({});
  const [statusBusy, setStatusBusy] = useState({});

  async function loadPartners({ append = false, nextToken = "" } = {}) {
    const payload = await listAdminAffiliatePartners({ limit: 100, nextToken });
    setState((current) => ({
      ...current,
      loading: false,
      loadingMore: false,
      partners: append ? [...current.partners, ...(payload.data || [])] : (payload.data || []),
      nextToken: payload.meta?.nextToken || "",
      error: "",
    }));
  }

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        await getCurrentUser();
      } catch (error) {
        if (active) setState({ loading: false, signedIn: false, isAdmin: false, partners: [], nextToken: "", loadingMore: false, error: error?.message || "" });
        return;
      }

      let email = "";
      try {
        const attrs = await fetchUserAttributes();
        email = String(attrs.email || "").trim().toLowerCase();
      } catch (error) {
        if (active) setState({ loading: false, signedIn: true, isAdmin: false, partners: [], nextToken: "", loadingMore: false, error: error?.message || "Could not verify admin access." });
        return;
      }

      if (!active) return;
      if (email !== ADMIN_EMAIL) {
        setState({ loading: false, signedIn: true, isAdmin: false, partners: [], nextToken: "", loadingMore: false, error: "" });
        return;
      }

      setState((current) => ({ ...current, signedIn: true, isAdmin: true }));
      try {
        const payload = await listAdminAffiliatePartners({ limit: 100 });
        if (active) setState({ loading: false, signedIn: true, isAdmin: true, partners: payload.data || [], nextToken: payload.meta?.nextToken || "", loadingMore: false, error: "" });
      } catch (error) {
        if (active) setState({ loading: false, signedIn: true, isAdmin: true, partners: [], nextToken: "", loadingMore: false, error: error?.message || "Could not load partner admin data." });
      }
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  async function setStatus(partnerCode, status) {
    if (statusBusy[partnerCode]) return;
    const reviewNote = String(reviewNotes[partnerCode] || "").trim();
    if (reviewNote.length < 20) {
      setReviewErrors((current) => ({ ...current, [partnerCode]: "Add at least 20 characters before changing status." }));
      return;
    }
    setStatusBusy((current) => ({ ...current, [partnerCode]: true }));
    setReviewErrors((current) => ({ ...current, [partnerCode]: "" }));
    setState((current) => ({ ...current, error: "" }));
    try {
      const payload = await patchAdminAffiliatePartner(partnerCode, { status, reviewNote });
      const updatedPartner = payload.data;
      if (updatedPartner) {
        setState((current) => ({
          ...current,
          partners: current.partners.map((partner) =>
            partner.partnerCode === partnerCode ? updatedPartner : partner,
          ),
          error: "",
        }));
      }
      setReviewNotes((current) => ({ ...current, [partnerCode]: "" }));
    } catch (error) {
      setState((current) => ({ ...current, error: error?.message || "Could not update partner status." }));
    } finally {
      setStatusBusy((current) => ({ ...current, [partnerCode]: false }));
    }
  }

  async function loadMorePartners() {
    if (!state.nextToken || state.loadingMore) return;
    setState((current) => ({ ...current, loadingMore: true }));
    try {
      await loadPartners({ append: true, nextToken: state.nextToken });
    } catch (error) {
      setState((current) => ({ ...current, loadingMore: false, error: error?.message || "Could not load more partners." }));
    }
  }

  const totals = useMemo(() => state.partners.reduce((acc, partner) => {
    const metrics = partner.metrics || {};
    acc.pageViews += Number(metrics.pageViews || 0);
    acc.iosClicks += Number(metrics.iosClicks || 0);
    acc.iosStandardClicks += Number(metrics.iosStandardClicks || 0);
    acc.androidClicks += Number(metrics.androidClicks || 0);
    acc.androidStandardClicks += Number(metrics.androidStandardClicks || 0);
    acc.outboundClicks += Number(metrics.outboundClicks || 0);
    return acc;
  }, { pageViews: 0, iosClicks: 0, iosStandardClicks: 0, androidClicks: 0, androidStandardClicks: 0, outboundClicks: 0 }), [state.partners]);

  if (state.loading) {
    return <main className="affiliate-page"><div className="affiliate-loading">Loading partner admin...</div></main>;
  }

  if (!state.signedIn) {
    return <main className="affiliate-page"><AuthRequiredPanel returnTo="/partners/admin" /></main>;
  }

  if (!state.isAdmin) {
    return (
      <main className="affiliate-page">
        <section className="affiliate-panel">
          <p className="affiliate-kicker">Access denied</p>
          <h1>Partner admin is limited to ArmorySync support.</h1>
        </section>
      </main>
    );
  }

  return (
    <main className="affiliate-page">
      <Helmet>
        <title>Partner Admin | ArmorySync</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <section className="affiliate-dashboard-hero affiliate-admin-hero">
        <div>
          <p className="affiliate-kicker">Admin</p>
          <h1>Partner review queue</h1>
          <p>
            Review applications, change partner status, and use campaign traffic as supporting evidence.
            {state.nextToken ? " Load more to include the next page of partner records." : ""}
          </p>
        </div>
      </section>
      <section className="affiliate-metrics-grid affiliate-admin-metrics" aria-label="Loaded partner performance summary">
        <MetricCard label="Landing views" value={totals.pageViews} />
        <MetricCard label="iOS campaign clicks" value={totals.iosClicks} />
        <MetricCard label="iOS standard clicks" value={totals.iosStandardClicks} />
        <MetricCard label="Android clicks" value={totals.androidClicks} />
        <MetricCard label="Android standard clicks" value={totals.androidStandardClicks} />
        <MetricCard label="Outbound clicks" value={totals.outboundClicks} />
      </section>
      <section className="affiliate-admin-table" aria-label="Partner list">
        {state.partners.map((partner) => (
          <article key={partner.partnerCode} className="affiliate-admin-row">
            <div className="affiliate-admin-main">
              <div className="affiliate-admin-titleline">
                <strong>{partner.displayName}</strong>
                <span className={`affiliate-status affiliate-status-${partner.status || "pending"}`}>{partner.status || "pending"}</span>
              </div>
              <dl className="affiliate-admin-meta">
                <div>
                  <dt>Owner</dt>
                  <dd>{partner.ownerEmail}</dd>
                </div>
                <div>
                  <dt>Code</dt>
                  <dd>{partner.partnerCode}</dd>
                </div>
                <div>
                  <dt>Type</dt>
                  <dd>{formatPartnerType(partner.partnerType)}</dd>
                </div>
              </dl>
              {partner.websiteOrSocialUrl && (
                <a className="affiliate-admin-link" href={partner.websiteOrSocialUrl} target="_blank" rel="noreferrer">
                  {partner.websiteOrSocialUrl}
                </a>
              )}
              {partner.notes && <p>{partner.notes}</p>}
              {partner.lastReviewNote && (
                <p className="affiliate-admin-review-note">
                  Last review: {partner.lastReviewNote}
                  {partner.reviewedBy ? ` · ${partner.reviewedBy}` : ""}
                </p>
              )}
            </div>
            <div className="affiliate-admin-stats">
              <span><strong>{formatNumber(partner.metrics?.pageViews)}</strong> views</span>
              <span><strong>{formatNumber(partner.metrics?.outboundClicks)}</strong> store clicks</span>
              <span><strong>{formatNumber(partner.metrics?.iosStandardClicks)}</strong> standard iOS</span>
              <span><strong>{formatNumber(partner.metrics?.androidStandardClicks)}</strong> standard Android</span>
            </div>
            <div className="affiliate-admin-actions">
              <textarea
                value={reviewNotes[partner.partnerCode] || ""}
                onChange={(event) => {
                  setReviewNotes((current) => ({ ...current, [partner.partnerCode]: event.target.value }));
                  setReviewErrors((current) => ({ ...current, [partner.partnerCode]: "" }));
                }}
                placeholder="Review note required before status change"
                rows={3}
                disabled={Boolean(statusBusy[partner.partnerCode])}
              />
              <p className={`affiliate-admin-note-help ${reviewErrors[partner.partnerCode] ? "affiliate-admin-note-error" : ""}`}>
                {reviewErrors[partner.partnerCode] || `${Math.min(String(reviewNotes[partner.partnerCode] || "").trim().length, 20)}/20 characters required`}
              </p>
              <button className="affiliate-admin-approve" onClick={() => setStatus(partner.partnerCode, "active")} disabled={partner.status === "active" || Boolean(statusBusy[partner.partnerCode])}>Approve</button>
              <button className="affiliate-admin-pause" onClick={() => setStatus(partner.partnerCode, "paused")} disabled={partner.status === "paused" || Boolean(statusBusy[partner.partnerCode])}>Pause</button>
              <button className="affiliate-admin-reject" onClick={() => setStatus(partner.partnerCode, "rejected")} disabled={partner.status === "rejected" || Boolean(statusBusy[partner.partnerCode])}>Reject</button>
            </div>
          </article>
        ))}
        {state.partners.length === 0 && <p className="affiliate-soft-note">No partner applications yet.</p>}
      </section>
      {state.nextToken && (
        <div className="affiliate-admin-load-more">
          <button className="affiliate-button affiliate-button-secondary" type="button" onClick={loadMorePartners} disabled={state.loadingMore}>
            {state.loadingMore ? "Loading..." : "Load more partners"}
          </button>
        </div>
      )}
      {state.error && <p className="affiliate-soft-note">{state.error}</p>}
    </main>
  );
}

export const PartnerProgram_Page = { path: "/partners", element: <PartnerProgramPage /> };
export const PartnerOnboarding_Page = { path: "/partners/onboarding", element: <PartnerOnboardingPage /> };
export const PartnerDashboard_Page = { path: "/partners/dashboard", element: <PartnerDashboardPage /> };
export const ReferralLanding_Page = { path: "/r/:partnerCode", element: <ReferralLandingPage /> };
export const PartnerAdmin_Page = { path: "/partners/admin", element: <PartnerAdminPage /> };
